import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Handshake, TrendingDown, AlertCircle, Percent, Loader2, Sparkles } from "lucide-react";

/**
 * Dashboard "Salud del catálogo de proveedores".
 * Muestra KPIs agregados de labs con/sin condición, media efectiva y ahorro potencial.
 */
export default function SupplierHealthDashboard({ dispId, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    const q = dispId ? `?disp_id=${encodeURIComponent(dispId)}` : "";
    api
      .get(`/supplier-agreements/health-dashboard${q}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "No se pudo cargar"))
      .finally(() => setLoading(false));
  }, [dispId, refreshKey]);

  if (loading)
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 text-sm text-slate-500 flex items-center gap-2" data-testid="health-dashboard-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Calculando salud del catálogo…
      </div>
    );
  if (err)
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6 text-sm text-rose-700" data-testid="health-dashboard-error">
        {err}
      </div>
    );
  if (!data) return null;

  const pctLabsWithAgreement = data.n_labs_total
    ? Math.round((data.n_labs_with_agreement / data.n_labs_total) * 100)
    : 0;

  return (
    <div
      className="mb-6 bg-gradient-to-br from-slate-950 to-slate-900 rounded-2xl p-6 text-white"
      data-testid="health-dashboard"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-white/10 rounded-lg">
          <Sparkles className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-300">
            Salud del catálogo
          </div>
          <div className="text-lg font-bold">Panorama de proveedores</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          icon={<Handshake className="h-4 w-4 text-emerald-300" />}
          label="Labs con condición"
          value={data.n_labs_with_agreement}
          suffix={`/ ${data.n_labs_total}`}
          subtitle={`${pctLabsWithAgreement}% del total`}
          testid="kpi-labs-agreement"
        />
        <Kpi
          icon={<Percent className="h-4 w-4 text-violet-300" />}
          label="Con lineal"
          value={data.n_labs_with_linear}
          subtitle={`${data.n_labs_with_exceptions} con excepciones`}
          testid="kpi-labs-linear"
        />
        <Kpi
          icon={<Percent className="h-4 w-4 text-sky-300" />}
          label="Media efectiva actual"
          value={`${data.avg_effective_weighted_pct.toFixed(2).replace(".", ",")}%`}
          subtitle={`Aritmética vademecum: ${data.avg_arithmetic_pct.toFixed(2).replace(".", ",")}%`}
          testid="kpi-avg-effective"
        />
        <Kpi
          icon={<TrendingDown className="h-4 w-4 text-emerald-300" />}
          label="Ahorro potencial"
          value={`${data.total_saving_potential_eur.toLocaleString("es-ES", { maximumFractionDigits: 0 })} €`}
          subtitle="Si compraras cada GH al mejor lab con condición"
          testid="kpi-saving-potential"
          highlight
        />
      </div>

      {data.n_molecules_without_agreement > 0 && (
        <div
          className="mt-4 flex items-start gap-3 rounded-xl bg-amber-500/10 border border-amber-400/30 p-3 text-xs text-amber-100"
          data-testid="kpi-no-agreement-alert"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-300" />
          <div>
            <strong>{data.n_molecules_without_agreement}</strong> molécula(s) genérica(s) que dispensas
            no tienen a NINGÚN laboratorio con condición cargada. Considera negociar con alguno de sus proveedores.
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, subtitle, suffix, highlight, testid }) {
  return (
    <div
      className={`rounded-xl p-3 ${highlight ? "bg-emerald-500/15 border border-emerald-400/30" : "bg-white/5 border border-white/10"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <div className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
          {label}
        </div>
      </div>
      <div className="flex items-baseline gap-1">
        <div className={`text-2xl font-bold font-mono-num ${highlight ? "text-emerald-300" : "text-white"}`}>
          {value}
        </div>
        {suffix && <div className="text-xs text-white/50 font-mono-num">{suffix}</div>}
      </div>
      {subtitle && (
        <div className="text-[10px] text-white/50 mt-1 truncate" title={subtitle}>
          {subtitle}
        </div>
      )}
    </div>
  );
}
